/*
 * Tandy Club — shared Yotpo SDK access. One place waits for swellAPI, fetches
 * each resource once, and hands the same cached promise to the status, activity
 * and program scripts. So the page makes a single getCustomerDetails (and a
 * single getVipTiers) instead of each block calling its own, runs one poll loop
 * instead of three, and keeps the cold-load "is the data really here yet?" retry
 * in exactly one place.
 *
 *   window.TandyClub.customer()           -> Promise<details|null>  (with_history)
 *   window.TandyClub.vipTiers()           -> Promise<array>
 *   window.TandyClub.redemptionOptions()  -> Promise<array>
 *   window.TandyClub.campaigns()          -> Promise<array>
 *
 * Loaded once (from the section) before the consumer scripts; each consumer
 * guards on window.TandyClub and no-ops if it is somehow absent.
 */
(function () {
  'use strict';
  if (window.TandyClub) return;

  var POLL_MS = 300, MAX_TRIES = 60;

  // ?tcdebug live readout — a self-updating line pinned to the top of the page
  // reporting the SDK boot/identify state, so broken loads are diagnosable from
  // a screenshot without devtools. Zero overhead unless debug is on.
  var _t0 = Date.now();
  var _dbg = { api: 'waiting for swellAPI…', cust: '-', watch: '-' };
  function dbgSummary(d) {
    if (!d || typeof d !== 'object') return 'empty payload';
    var tier = d.vipTier && d.vipTier.name ? d.vipTier.name : '-';
    return 'email=' + (d.email || '-') + ' · optIn=' + d.isOptIn + ' · tier=' + tier + ' · points=' + d.pointsBalance;
  }
  (function dbgLoop() {
    if (!window.tcDebug) return;
    var el = document.getElementById('tc-sdk-debug');
    if (!el && document.body) {
      el = document.createElement('pre');
      el.id = 'tc-sdk-debug';
      el.className = 'tc-status__debug tc-debug-line';
      el.style.cssText = 'position:sticky;top:0;z-index:9999;background:#fffafa;';
      document.body.insertBefore(el, document.body.firstChild);
    }
    if (el) el.textContent = '[Tandy Club SDK] t=' + Math.round((Date.now() - _t0) / 1000) + 's · ' + _dbg.api + ' · customer: ' + _dbg.cust + ' · watcher: ' + _dbg.watch;
    setTimeout(dbgLoop, 1000);
  })();

  // Resolve once swellAPI is present and queryable. The SDK fires `swell:setup`
  // when ready; we also poll as a fallback. Rejects after the ceiling (~18s) so
  // callers fall back instead of hanging forever.
  function whenReady() {
    return new Promise(function (resolve, reject) {
      var n = 0, done = false;
      function settle() {
        if (done) return true;
        var api = window.swellAPI;
        if (api && typeof api.getCustomerDetails === 'function') {
          done = true;
          _dbg.api = 'swellAPI ready @' + Math.round((Date.now() - _t0) / 1000) + 's';
          resolve(api);
          return true;
        }
        return false;
      }
      if (settle()) return;
      document.addEventListener('swell:setup', settle);
      (function tick() {
        if (done) return;
        if (settle()) return;
        if (++n >= MAX_TRIES) { _dbg.api = 'swellAPI NOT LOADED after ~18s'; reject(new Error('swellAPI unavailable')); return; }
        setTimeout(tick, POLL_MS);
      })();
    });
  }

  // Cold load: the Yotpo SDK often returns an empty/default object on the first
  // calls before it finishes identifying a logged-in customer (balance 0, empty
  // arrays). hasMemberData = the payload carries real member data.
  function hasMemberData(d) {
    return (typeof d.pointsBalance === 'number' && d.pointsBalance > 0) || d.vipTier || d.isOptIn === true ||
      (d.actionHistoryItems && d.actionHistoryItems.length) ||
      (d.pointRedemptions && d.pointRedemptions.length);
  }
  // Treat a response as final only when it has real member data, OR — for shoppers
  // the server does NOT flag as likely members — when it's an identified response
  // (so genuine non-members don't wait). likelyMember (window.tcLikelyMember, set
  // server-side from the Yotpo tier tag) means "keep waiting for the real payload
  // instead of accepting an empty one." The tag is only a retry hint; everything
  // shown still comes from the SDK.
  function isLoaded(d, likelyMember) {
    if (!d || typeof d !== 'object') return false;
    if (hasMemberData(d)) return true;
    if (likelyMember) return false;
    if (d.email || d.isOptIn !== undefined) return true;
    return false;
  }

  function attemptFetch(api) {
    // Older SDKs reject the options form — fall back to the bare call.
    return Promise.resolve().then(function () { return api.getCustomerDetails({ with_history: true }); })
      .catch(function () { return api.getCustomerDetails(); });
  }

  // getCustomerDetails with the cold-load retry. Resolves with the data once it's
  // real (or with whatever we have at the ceiling), never rejects — callers get
  // null only if the SDK never came up. If a logged-in shopper's payload is still
  // empty when we resolve (Yotpo occasionally identifies the customer very late),
  // lateWatch keeps polling in the background and broadcasts the real data when
  // it lands, so the page recovers without a reload.
  function fetchCustomer(api) {
    var likelyMember = !!window.tcLikelyMember;
    return new Promise(function (resolve) {
      var n = 0;
      function finish(d) {
        _dbg.cust = 'resolved (' + dbgSummary(d) + ')';
        if (window.tcLoggedIn && !(d && hasMemberData(d))) lateWatch(api);
        resolve(d || null);
      }
      (function tick() {
        _dbg.cust = 'identifying… try ' + (n + 1) + '/' + MAX_TRIES;
        attemptFetch(api).then(function (d) {
          _dbg.cust = 'try ' + (n + 1) + ': ' + dbgSummary(d);
          if (isLoaded(d, likelyMember) || ++n >= MAX_TRIES) return finish(d);
          setTimeout(tick, POLL_MS);
        }).catch(function (e) {
          _dbg.cust = 'try ' + (n + 1) + ' threw: ' + (e && e.message || e);
          if (++n >= MAX_TRIES) return finish(null);
          setTimeout(tick, POLL_MS);
        });
      })();
    });
  }

  // Second-chance watcher: poll gently (2.5s for ~4 min) after an empty resolve.
  // When real member data appears, refresh the cache and tell the consumer
  // scripts (status/activity/coupons/gate) to re-render with it. For genuine
  // non-members nothing ever fires and the polling just times out quietly.
  function lateWatch(api) {
    var n = 0;
    var iv = setInterval(function () {
      attemptFetch(api).then(function (d) {
        if (d && hasMemberData(d)) {
          clearInterval(iv);
          _dbg.watch = 'lateWatch: recovered (' + dbgSummary(d) + ')';
          _c = Promise.resolve(d); // late callers get the good data too
          try { document.dispatchEvent(new CustomEvent('tandyclub:customer-updated', { detail: d })); } catch (e) {}
        } else if (++n >= 96) { clearInterval(iv); _dbg.watch = 'lateWatch: gave up after 4 min (' + dbgSummary(d) + ')'; }
        else { _dbg.watch = 'lateWatch ' + n + '/96 (' + dbgSummary(d) + ')'; }
      }).catch(function () { if (++n >= 96) clearInterval(iv); });
    }, 2500);
  }

  // swellAPI itself can land later than whenReady's ~18s fast-poll ceiling
  // (slow connections, consent managers, CDN hiccups). Without this, customer()
  // caches null forever and the page never recovers. Keep watching quietly for
  // ~4 more minutes; when the SDK finally appears, fetch for real, refresh every
  // cache and broadcast so the consumers re-render.
  function lateBoot() {
    var n = 0;
    var iv = setInterval(function () {
      var api = window.swellAPI;
      if (api && typeof api.getCustomerDetails === 'function') {
        clearInterval(iv);
        _dbg.api = 'swellAPI arrived LATE @' + Math.round((Date.now() - _t0) / 1000) + 's';
        _t = _r = _cam = undefined; // list getters cached [] on the failed boot
        fetchCustomer(api).then(function (d) {
          if (!d) return;
          _c = Promise.resolve(d);
          try { document.dispatchEvent(new CustomEvent('tandyclub:customer-updated', { detail: d })); } catch (e) {}
        });
      } else if (++n >= 96) { clearInterval(iv); _dbg.watch = 'lateBoot: swellAPI never appeared (4 min)'; }
      else { _dbg.watch = 'lateBoot: waiting for swellAPI ' + n + '/96'; }
    }, 2500);
  }

  function listGetter(name) {
    return whenReady().then(function (api) {
      if (typeof api[name] !== 'function') return [];
      return Promise.resolve().then(function () { return api[name](); }).then(function (r) { return Array.isArray(r) ? r : []; });
    }).catch(function () { return []; });
  }

  var _c, _t, _r, _cam;
  window.TandyClub = {
    isLoaded: isLoaded,
    customer: function () { return _c || (_c = whenReady().then(fetchCustomer).catch(function () { lateBoot(); return null; })); },
    vipTiers: function () { return _t || (_t = listGetter('getVipTiers')); },
    redemptionOptions: function () { return _r || (_r = listGetter('getActiveRedemptionOptions')); },
    campaigns: function () { return _cam || (_cam = listGetter('getActiveCampaigns')); }
  };
})();

